"use client";
import AccordionSingleProduct from "@components/commerce/product/accordion-single-product/AccordionSingleProduct";
import ProductImagesContainer from "@components/commerce/product/image/ProductImagesContainer";
import { Breadcrumbs } from "@components/commerce/categories/breadcrumbs/Breadcrumbs";
import ProductBuyCard from "@components/commerce/product/right-panel/buy-card/ProductBuyCard";
import ProductRecommendations from "@components/commerce/product/recommendations/DeferredProductRecommendations";
import { AlgoliaRecommendModel } from "@lib/algolia/types";
import type { Recommendations } from "@lib/algolia/utils";
import type { Product, ProductVariant } from "@lib/product/types";
import type { AlgoliaRelatedProduct } from "@lib/wrappers/algolia/types";
import { H } from "@ecco/ui/a11y-heading";
import { Box } from "@ecco/ui/box";
import { Grid, GridItem } from "@ecco/ui/grid";
import space from "@ecco/ui/tokens/space";
import { useInView } from "@ecco/ui/use-in-view";
import { findMostCommonCategories } from "@lib/utils/breadcrumbsUtility";
import { getEnrichedPageTitle } from "@lib/utils/attributesUtils";
import { isFootwear } from "@lib/utils/pdp/pdpShoeSizes";
import { getValidSaleBadge } from "@lib/utils/pdp/saleBadgeUtils";
import { RichText } from "@components/cms/rich-text-renderer/RichText";
import { useGlobalContent } from "@app/[locale]/GlobalContentProvider";
import { useMicrocopy } from "@lib/contentful/microcopy/MicrocopyContext";
import { CommonCMS } from "@lib/constants/contentful";
import { useLocale } from "next-intl";
import type { RefObject } from "react";
import { useEffect, useMemo, useRef } from "react";
import { useStore } from "@store/index";
import { useBreadcrumbItems } from "@components/commerce/categories/breadcrumbs/useBreadcrumbItems";
import { ReturnLabel } from "@components/cms/return-label/ReturnLabel";
import { PRODUCT_CAROUSEL_DEFAULT_TILE_WIDTHS } from "@lib/constants/carousel";
import { getAvailableSkus } from "@lib/utils/productUtility";
import { useFeatureFlag } from "@lib/feature-flags/FeatureFlagsContext";

interface ProductPurchaseSectionProps {
    product: Product;
    defaultPrice: any;
    onAddToCart: (variant: ProductVariant, analytics?: boolean) => Promise<void>;
    calloutDiscountTitle: string;
    calloutDiscountDescription: string;
    relatedProduct?: AlgoliaRelatedProduct;
    isFetchingProduct: boolean;
    recommendations: Recommendations;
    headlineProductNameRef: RefObject<HTMLHeadingElement | null>;
    onStickyChange: (isSticky: boolean) => void;
}

export const ProductPurchaseSection = ({
    product,
    defaultPrice,
    onAddToCart,
    calloutDiscountTitle,
    calloutDiscountDescription,
    relatedProduct,
    isFetchingProduct,
    recommendations,
    headlineProductNameRef,
    onStickyChange,
}: ProductPurchaseSectionProps) => {
    const { name: productName, attributes: productAttributes, media, articleNumber } = product;
    const isReturnLabelEnabled = useFeatureFlag("isReturnLabelEnabled");
    const locale = useLocale();
    const { getMultiple } = useMicrocopy();
    const { siteSettings } = useGlobalContent();
    const { theme } = siteSettings.promotions;
    const { shippingReturns = null, deliveryUsps: deliveryUSPs = [] } = siteSettings.product;
    const { [CommonCMS.lookingSimilarMC]: lookingSimilar } = getMultiple(CommonCMS.page, [
        CommonCMS.lookingSimilarMC,
    ]);

    const shippingAndReturns = useMemo(
        () => (shippingReturns ? <RichText data={shippingReturns} /> : null),
        [shippingReturns]
    );

    const productSizeVisible = useRef(true);
    const productBuyCardVisible = useRef(true);
    const updateStickyState = () => {
        onStickyChange(productBuyCardVisible.current ? false : !productSizeVisible.current);
    };
    const skus = useMemo(() => getAvailableSkus(product.variants), [product.variants]);
    const { ref: productBuyCardRef } = useInView({
        threshold: 0,
        rootMargin: `0% 0% 1px 0%`,
        trackVisibility: true,
        delay: 100,
        onChange: (inView) => {
            productBuyCardVisible.current = inView;
            updateStickyState();
        },
    });

    const { ref: sizeContainerRef } = useInView({
        threshold: 0,
        rootMargin: `0% 0% 0% 0%`,
        trackVisibility: true,
        delay: 100,
        onChange: (inView) => {
            productSizeVisible.current = inView;
            updateStickyState();
        },
    });

    const altTagPattern = getEnrichedPageTitle(productAttributes, productName, locale, "-");
    const isFootwearProduct = isFootwear(productAttributes, locale);
    const saleBadge = getValidSaleBadge(relatedProduct?.eaBadge);

    const lastVisitedPlp = useStore((state) => state.data.products.lastVisitedPlp);
    const setLastVisitedPlp = useStore((state) => state.setLastVisitedPlp);

    const plpSegments = useMemo(() => {
        return lastVisitedPlp
            ? lastVisitedPlp.split("?")[0].split("/").filter(Boolean)
            : findMostCommonCategories(relatedProduct?.categories ?? []);
    }, [articleNumber, locale]);

    useEffect(() => {
        setLastVisitedPlp(null);
    }, [articleNumber]);

    const breadcrumbItems = useBreadcrumbItems({
        pathSegments: plpSegments,
        locale,
        trailing: { label: productName.toUpperCase(), link: "" },
    });

    return (
        <Grid
            as="main"
            gap={[0, 0, 0, 5]}
            gridTemplateColumns={["100%", "100%", "100%", "repeat(2, minmax(0, 1fr))"]}
            mt={[2, 2, 2, 0]}
            mb={20}
        >
            <GridItem position="relative">
                <Box
                    position={"absolute"}
                    w="auto"
                    h="auto"
                    left={[3, 3, 3, 6]}
                    top={[0, 0, 0, 0]}
                    zIndex={2}
                    right={0}
                >
                    <Breadcrumbs
                        items={breadcrumbItems}
                        renderLastAsText={true}
                        px={space["0.2"]}
                        pt={{ base: 2, lg: 4 }}
                    />
                </Box>
                <ProductImagesContainer
                    media={media}
                    altTagPattern={altTagPattern}
                    sku={product.sku}
                />
            </GridItem>
            <GridItem top={[12, 12, 12, 16]} paddingRight={[0, 0, 0, 9]}>
                <ProductBuyCard
                    product={product}
                    initialProductPrice={defaultPrice}
                    onAddToCart={onAddToCart}
                    calloutDiscountTitle={calloutDiscountTitle}
                    calloutDiscountDescription={calloutDiscountDescription}
                    imageThumbnails={relatedProduct?.imageThumbnails}
                    saleBadge={saleBadge}
                    eccoClubBadge={relatedProduct?.eccoClubBadge}
                    productBuyCardRef={productBuyCardRef}
                    headlineProductNameRef={headlineProductNameRef}
                    sizeContainerRef={sizeContainerRef}
                    isFootwear={isFootwearProduct}
                    isFetchingProduct={isFetchingProduct}
                    recommendations={recommendations.similarProducts}
                />
                <Box px={[3, 3, 3, 0]} w={"100%"}>
                    {isReturnLabelEnabled && <ReturnLabel />}
                    <klarna-placement
                        data-key="credit-promotion-auto-size"
                        data-locale={locale}
                        data-purchase-amount={defaultPrice?.value?.centAmount}
                    />
                </Box>
                <AccordionSingleProduct
                    attributesRaw={productAttributes}
                    productName={productName}
                    image={media[0]}
                    locale={locale}
                    uspInfos={deliveryUSPs}
                    skus={skus}
                    shippingAndReturns={shippingAndReturns}
                />
                {recommendations.similarProducts.length > 0 && (
                    <Box maxW={["100vw", "100vw", "100vw", "50vw", "50vw", "720px"]} mt={4}>
                        <H
                            px={[3, 3, 3, 0]}
                            level={"h5"}
                            fontSize={["mobileH5", "tabletH5", "desktopH5"]}
                            /* In order to get the transition on the scrollbar, we need overwrite this value with important to avoid hide the headline. TBD by Designers. */
                            color="black !important"
                            aria-label={lookingSimilar}
                            textTransform={"uppercase"}
                        >
                            {lookingSimilar}
                        </H>
                        <ProductRecommendations
                            headline={""}
                            theme={theme}
                            recommendations={recommendations.similarProducts}
                            carouselHeight={["auto", "auto", "auto", "30vw", "28vw", "406px"]}
                            tileWidth={[
                                ...PRODUCT_CAROUSEL_DEFAULT_TILE_WIDTHS,
                                "calc(50vw/3.5)",
                                "205px",
                            ]}
                            model={AlgoliaRecommendModel.LOOKING_SIMILAR}
                            hasCompactBadgesOnDesktop
                        />
                    </Box>
                )}
            </GridItem>
        </Grid>
    );
};
